import type {RoadmapAuditFindingItem} from 'client/models/RoadmapAuditFindingItem';
import type {RoadmapAuditHierarchyBreadcrumb} from 'client/models/RoadmapAuditHierarchyBreadcrumb';

export const UNASSIGNED_AUDIT_HIERARCHY_ID = '__unassigned_hierarchy__';

export interface AuditFindingHierarchyNode {
  id: string;
  breadcrumb: RoadmapAuditHierarchyBreadcrumb | null;
  children: AuditFindingHierarchyNode[];
  items: RoadmapAuditFindingItem[];
  descendantCount: number;
}

interface MutableAuditFindingHierarchyNode extends Omit<
  AuditFindingHierarchyNode,
  'children' | 'descendantCount'
> {
  children: MutableAuditFindingHierarchyNode[];
  childById: Map<string, MutableAuditFindingHierarchyNode>;
}

const createMutableNode = (
  id: string,
  breadcrumb: RoadmapAuditHierarchyBreadcrumb | null
): MutableAuditFindingHierarchyNode => ({
  id,
  breadcrumb,
  children: [],
  childById: new Map(),
  items: [],
});

const finalizeNode = (
  node: MutableAuditFindingHierarchyNode
): {node: AuditFindingHierarchyNode; initiativeKeys: Set<string>} => {
  const initiativeKeys = new Set(node.items.map((item) => item.initiative.key));
  const children = node.children.map((child) => {
    const finalized = finalizeNode(child);
    finalized.initiativeKeys.forEach((key) => initiativeKeys.add(key));
    return finalized.node;
  });

  return {
    node: {
      id: node.id,
      breadcrumb: node.breadcrumb,
      children,
      items: node.items,
      descendantCount: initiativeKeys.size,
    },
    initiativeKeys,
  };
};

export const buildAuditFindingHierarchy = (
  items: RoadmapAuditFindingItem[]
): AuditFindingHierarchyNode[] => {
  const roots: MutableAuditFindingHierarchyNode[] = [];
  const rootById = new Map<string, MutableAuditFindingHierarchyNode>();
  const seenInitiativeKeys = new Set<string>();

  items.forEach((item) => {
    if (seenInitiativeKeys.has(item.initiative.key)) return;
    seenInitiativeKeys.add(item.initiative.key);

    const breadcrumbs = item.hierarchy ?? [];
    if (breadcrumbs.length === 0) {
      let orphanRoot = rootById.get(UNASSIGNED_AUDIT_HIERARCHY_ID);
      if (!orphanRoot) {
        orphanRoot = createMutableNode(UNASSIGNED_AUDIT_HIERARCHY_ID, null);
        roots.push(orphanRoot);
        rootById.set(UNASSIGNED_AUDIT_HIERARCHY_ID, orphanRoot);
      }
      orphanRoot.items.push(item);
      return;
    }

    let parent: MutableAuditFindingHierarchyNode | null = null;
    const path: string[] = [];
    for (const breadcrumb of breadcrumbs) {
      path.push(breadcrumb.key);
      const id = JSON.stringify(path);
      const siblings: Map<string, MutableAuditFindingHierarchyNode> = parent
        ? parent.childById
        : rootById;
      let node: MutableAuditFindingHierarchyNode | undefined = siblings.get(id);
      if (!node) {
        node = createMutableNode(id, breadcrumb);
        siblings.set(id, node);
        if (parent) {
          parent.children.push(node);
        } else {
          roots.push(node);
        }
      }
      parent = node;
    }
    if (parent) parent.items.push(item);
  });

  return roots.map((root) => finalizeNode(root).node);
};

export const getAuditHierarchyNodeIds = (nodes: AuditFindingHierarchyNode[]): string[] =>
  nodes.flatMap((node) => [node.id, ...getAuditHierarchyNodeIds(node.children)]);
